import "@pique/component/theme/global.css";
import "@pique/component/theme/theme.css";

import { CacheProvider } from "@emotion/react";
import { NovuProvider } from "@novu/react";
import { AffineContext } from "@pique/component/context";
import { GlobalLoading } from "@pique/component/global-loading";
import { AppFallback } from "@pique/core/components/pique/app-container";
import { PiqueErrorBoundary } from "@pique/core/components/pique/pique-error-boundary";
import { NOVU_APP_IDENTIFIER } from "@pique/core/env/novu";
import { configureCommonModules } from "@pique/core/modules";
import { AuthService } from "@pique/core/modules/cloud/services/auth";
import {
	CalendarAttendeeSchema,
	CalendarEventSchema,
	ConferenceDataSchema,
} from "@pique/core/modules/properties/services/calendar-types";
import { configureLocalStorageStateStorageImpls } from "@pique/core/modules/storage";
import { CustomThemeModifier } from "@pique/core/modules/theme-editor";
import { configureBrowserWorkbenchModule } from "@pique/core/modules/workbench";
import {
	configureBrowserWorkspaceFlavours,
	configureWorkspaceEngineStorageProvider,
} from "@pique/core/modules/workspace-engine";
import { posthog } from "@pique/core/posthog";
import { router } from "@pique/core/router";
import createEmotionCache from "@pique/core/utils/create-emotion-cache";
import { createI18n, setUpLanguage } from "@pique/i18n";
import {
	Framework,
	FrameworkRoot,
	getCurrentStore,
	LifecycleService,
	useLiveData,
	useService,
} from "@toeverything/infra";
import type { PropsWithChildren, ReactElement } from "react";
import { lazy, Suspense } from "react";
import { RouterProvider } from "react-router-dom";

// Prevent default browser navigation on file/URL drops anywhere in the app.
// Do not stop propagation so editor handlers still work.
const preventExternalDropNavigation = (e: DragEvent) => {
	const dt = e.dataTransfer;
	if (!dt) return;
	const types = Array.from(dt.types || []);
	if (types.includes("Files") || types.includes("text/uri-list")) {
		e.preventDefault();
		// Forward to Blocksuite file manager via a custom event so it can
		// treat this like a host drop when outside the editor host.
		window.dispatchEvent(
			new CustomEvent("pique-global-drop", { detail: { event: e } }),
		);
	}
};

window.addEventListener("dragover", preventExternalDropNavigation, {
	passive: false,
});
window.addEventListener("drop", preventExternalDropNavigation, {
	passive: false,
});

if (!environment.isBrowser && environment.isDebug) {
	document.body.innerHTML = `<h1 style="color:red;font-size:5rem;text-align:center;">Don't run web entry in desktop app.</h1>`;
	throw new Error("Wrong distribution");
}

const cache = createEmotionCache();

const DevTools = lazy(() =>
	import("jotai-devtools").then((m) => ({ default: m.DevTools })),
);

const DebugProvider = ({ children }: PropsWithChildren): ReactElement => {
	return (
		<>
			<Suspense>{process.env.DEBUG_JOTAI === "true" && <DevTools />}</Suspense>
			{children}
		</>
	);
};

const future = {
	v7_startTransition: true,
} as const;

async function loadLanguage() {
	const i18n = createI18n();
	document.documentElement.lang = i18n.language;
	await setUpLanguage(i18n);
}

let languageLoadingPromise: Promise<void> | null = null;

const framework = new Framework();
configureCommonModules(framework);
configureBrowserWorkbenchModule(framework);
configureLocalStorageStateStorageImpls(framework);
configureBrowserWorkspaceFlavours(framework);
configureWorkspaceEngineStorageProvider(framework);
const frameworkProvider = framework.provider();

// setup application lifecycle events, and emit application start event
window.addEventListener("focus", () => {
	frameworkProvider.get(LifecycleService).applicationFocus();
});
frameworkProvider.get(LifecycleService).applicationStart();

// listen for deeplink events from tauri backend (structured + raw)
if (environment.isTauri) {
	import("@tauri-apps/api/event")
		.then(({ listen }) => {
			type AppInfo = { id: string; name: string };
			type MeetingDetectedPayload = {
				meetingApps: AppInfo[];
				allMicApps: AppInfo[];
			};
			type MeetingEndSignal = {
				appId: string;
				reason: string;
				missedMicPolls: number;
				missedMicSeconds: number;
				pollsObserved: number;
			};
			type MeetingEndedPayload = {
				endedApps?: string[];
				remainingTrackedApps?: string[];
				reason?: string;
				endedAppSignals?: MeetingEndSignal[];
			};

			const shownMeetingNotifications = new Map<string, number>();
			const shownMeetingEndedNotifications = new Map<string, number>();
			const MEETING_DEDUPE_MS = 30 * 1000;
			const MEETING_ENDED_DEDUPE_MS = 60 * 1000;

			listen("meeting-detected", (event) => {
				const payload = event.payload as MeetingDetectedPayload;
				const dedupeKey =
					payload.meetingApps.map((a) => a.id).join(",") || "unknown";

				const now = Date.now();
				const lastShown = shownMeetingNotifications.get(dedupeKey);
				if (lastShown && now - lastShown < MEETING_DEDUPE_MS) {
					return;
				}
				shownMeetingNotifications.set(dedupeKey, now);

				import("@pique/core/components/pique/setting-modal/atoms")
					.then(({ autoDetectMeetingsPreferenceAtom }) => {
						const store = getCurrentStore();
						const autoDetectEnabled = store.get(
							autoDetectMeetingsPreferenceAtom,
						);

						if (!autoDetectEnabled) {
							return;
						}

						import("@pique/core/services/transcription-service")
							.then(({ TranscriptionService, TranscriptionStatus }) => {
								const transcriptionService =
									frameworkProvider.get(TranscriptionService);
								const currentStatus =
									transcriptionService.transcriptionStatus$.value;

								if (
									currentStatus === TranscriptionStatus.RECORDING ||
									currentStatus === TranscriptionStatus.PAUSED
								) {
									return;
								}

								import("@pique/core/components/shared/notification-utils")
									.then(({ enhancedNotify, MEETING_ACTION_LABEL }) => {
										enhancedNotify
											.success(
												{
													title: "Heading Into A Call?",
													message: "Take notes with Pique!",
													url: `pique://notification/auto-detected-meeting?t=${Date.now()}`,
													actionLabel: MEETING_ACTION_LABEL,
													sound: false,
													timeout: 18000,
													rootAttrs: {
														style: {
															backgroundColor:
																"var(--affine-background-primary-color)",
															color: "var(--affine-text-primary-color)",
															borderColor: "var(--affine-border-color)",
															boxShadow: "var(--affine-menu-shadow)",
															borderRadius: "20px",
															border: "1px solid var(--affine-border-color)",
														},
													},
													action: {
														label: "Take Notes",
														onClick: () => {
															transcriptionService.triggerCreateDocWithRecording(
																{ shrinkWindow: true },
															);
														},
														autoClose: true,
														buttonProps: {
															style: {
																backgroundColor: "#0FEBC5",
																color: "#FFFFFF",
																boxShadow:
																	"0 0 8px 2px var(--affine-brand-color)40",
																borderRadius: "10px",
																padding: "6px 16px",
																fontWeight: "bold",
															},
														},
													},
												},
												{
													duration: 15000,
													position: "top-center",
													style: { borderRadius: "10px" },
												},
											)
											.catch(console.error);
									})
									.catch(console.error);
							})
							.catch(console.error);
					})
					.catch(console.error);
			}).catch(console.error);

			listen("meeting-ended", (event) => {
				const payload = event.payload as MeetingEndedPayload | undefined;
				const dedupeKey =
					payload?.endedApps?.slice().sort().join(",") ||
					payload?.reason ||
					"unknown";

				const now = Date.now();
				const lastShown = shownMeetingEndedNotifications.get(dedupeKey);
				if (lastShown && now - lastShown < MEETING_ENDED_DEDUPE_MS) {
					return;
				}
				shownMeetingEndedNotifications.set(dedupeKey, now);

				import("@pique/core/services/transcription-service")
					.then(({ TranscriptionService, TranscriptionStatus }) => {
						try {
							const transcriptionService =
								frameworkProvider.get(TranscriptionService);
							const currentStatus =
								transcriptionService.transcriptionStatus$.value;

							if (
								currentStatus !== TranscriptionStatus.RECORDING &&
								currentStatus !== TranscriptionStatus.PAUSED
							) {
								return;
							}

							transcriptionService.queueMeetingEndedPrompt({
								endedApps: payload?.endedApps ?? [],
								remainingTrackedApps: payload?.remainingTrackedApps ?? [],
								reason: payload?.reason ?? "unknown",
							});
						} catch (error) {
							console.error("Error handling meeting-ended event:", error);
						}
					})
					.catch(console.error);
			}).catch(console.error);

			let trayStartHandled = 0;
			const TRAY_START_DEDUPE_MS = 30 * 1000;

			listen("tray-start-recording", () => {
				const now = Date.now();
				if (now - trayStartHandled < TRAY_START_DEDUPE_MS) {
					return;
				}
				trayStartHandled = now;

				import("@pique/core/services/transcription-service")
					.then(({ TranscriptionService }) => {
						try {
							const transcriptionService =
								frameworkProvider.get(TranscriptionService);
							transcriptionService.triggerCreateDocWithRecording({
								shrinkWindow: true,
							});
						} catch (error) {
							console.error("Error starting recording from tray:", error);
						}
					})
					.catch(console.error);
			}).catch(console.error);

			const shownDeeplinkNotifications = new Map<string, number>();
			const DEEPLINK_DEDUPE_MS = 30 * 1000;

			const handleDeeplinkUrl = (url: string) => {
				const payload = { url };

				const dedupeKey = payload.url.split("?")[0];
				const now = Date.now();
				const lastHandled = shownDeeplinkNotifications.get(dedupeKey);
				if (lastHandled && now - lastHandled < DEEPLINK_DEDUPE_MS) {
					return;
				}
				shownDeeplinkNotifications.set(dedupeKey, now);

				if (payload.url.startsWith("pique://meeting-ended/generate-notes")) {
					import("@pique/core/services/transcription-service")
						.then(({ TranscriptionService }) => {
							try {
								const transcriptionService =
									frameworkProvider.get(TranscriptionService);
								transcriptionService.triggerAugmentation("deep_link");
							} catch (error) {
								console.error("Error triggering meeting notes:", error);
							}
						})
						.catch(console.error);
					return;
				}

				// Handle OAuth callback from deep link
				if (payload.url.startsWith("pique://auth/callback")) {
					try {
						const urlObj = new URL(payload.url);
						const sessionId = urlObj.searchParams.get("session");
						const errorParam = urlObj.searchParams.get("error");

						if (errorParam) {
							console.error("OAuth callback error:", errorParam);
							return;
						}

						if (sessionId) {
							import("@pique/core/modules/cloud/services/auth")
								.then(({ AuthService }) => {
									const authService = frameworkProvider.get(AuthService);
									authService
										.completeOAuthFromDeepLink(sessionId)
										.catch((error) => {
											console.error("OAuth completion failed:", error);
										});
								})
								.catch(console.error);
						} else {
							console.error("OAuth callback missing session parameter");
						}
					} catch (error) {
						console.error("Error handling auth callback deep link:", error);
					}
					return;
				}

				// Handle auto-detected meeting without calendar context
				if (
					payload.url.startsWith("pique://notification/auto-detected-meeting")
				) {
					import("@pique/core/services/transcription-service")
						.then(({ TranscriptionService }) => {
							try {
								const transcriptionService =
									frameworkProvider.get(TranscriptionService);
								transcriptionService.triggerCreateDocWithRecording({
									shrinkWindow: true,
								});
							} catch (error) {
								console.error(
									"Error triggering create doc with recording:",
									error,
								);
							}
						})
						.catch(console.error);
					return;
				}

				// Handle focus timer deep link to start pomodoro timer
				if (payload.url.startsWith("pique://focus-timer/")) {
					let itemId: string | undefined;
					try {
						const urlObj = new URL(payload.url);
						const path = urlObj.pathname.replace(/^\//, "");
						const parts = path.split("/").filter(Boolean);
						if (parts.length >= 1) {
							itemId = parts[0];
						}
					} catch {
						const rest = payload.url.replace("pique://focus-timer/", "");
						const [pathOnly] = rest.split("?");
						const parts = (pathOnly ?? "").split("/").filter(Boolean);
						if (parts.length >= 1) {
							itemId = parts[0];
						}
					}

					if (!itemId) return;

					import("@pique/core/atoms")
						.then(({ pendingFocusItemIdAtom }) => {
							const store = getCurrentStore();
							store.set(pendingFocusItemIdAtom, itemId ?? null);
						})
						.catch(console.error);
					return;
				}

				// Handle pre-read deep link to open pre-reading modal
				if (payload.url && payload.url.startsWith("pique://pre-read/")) {
					let workspaceId: string | undefined;
					let eventId: string | undefined;
					let searchParams: URLSearchParams | null = null;

					try {
						const urlObj = new URL(payload.url);
						const path = urlObj.pathname.replace(/^\//, "");
						const parts = path.split("/").filter(Boolean);
						if (parts.length >= 2) {
							workspaceId = parts[0];
							eventId = parts[1];
						} else if (parts.length === 1) {
							eventId = parts[0];
						}
						searchParams = urlObj.searchParams;
					} catch {
						const rest = payload.url.replace("pique://pre-read/", "");
						const [pathOnly, query = ""] = rest.split("?");
						const parts = (pathOnly ?? "").split("/").filter(Boolean);
						if (parts.length >= 2) {
							workspaceId = parts[0];
							eventId = parts[1];
						} else if (parts.length === 1) {
							eventId = parts[0];
						}
						searchParams = new URLSearchParams(query);
					}

					if (!eventId) return;

					const get = (k: string) => {
						const v = searchParams?.get(k);
						if (v === undefined || v === null) return undefined;
						return v === "" ? undefined : v;
					};

					const parseJsonish = (raw?: string): unknown => {
						if (!raw) return undefined;
						try {
							return JSON.parse(raw);
						} catch {
							const trimmed = raw.trim();
							const looksJson =
								(trimmed.startsWith("{") && trimmed.endsWith("}")) ||
								(trimmed.startsWith("[") && trimmed.endsWith("]"));
							if (!looksJson) return undefined;
							try {
								const s = trimmed
									.replace(/'([^']*)'\s*:/g, '"$1":')
									.replace(/:\s*'([^']*)'/g, ': "$1"')
									.replace(/'([^']*)'/g, '"$1"');
								return JSON.parse(s);
							} catch {
								return undefined;
							}
						}
					};

					import("@pique/core/atoms")
						.then(({ preReadingModalAtom }) => {
							const store = getCurrentStore();
							store.set(preReadingModalAtom, {
								open: true,
								eventId,
								workspaceId: workspaceId ?? null,
								summary: get("summary") ?? null,
								description: get("description") ?? null,
								location: get("location") ?? null,
								startTime: get("startTime") ?? null,
								endTime: get("endTime") ?? null,
								timeZone: get("timeZone") ?? null,
								hangoutLink: get("hangoutLink") ?? null,
								attendees: parseJsonish(get("attendees")) as unknown[] | null,
								conferenceData: parseJsonish(get("conferenceData")) ?? null,
								calendarId: get("calendarId") ?? null,
								source: get("source") ?? null,
							});
						})
						.catch(console.error);
					return;
				}

				// Parse notification deep link and start transcription if valid
				if (payload.url && payload.url.startsWith("pique://notification/")) {
					let workspaceId: string | undefined;
					let eventId: string | undefined;
					let searchParams: URLSearchParams | null = null;

					try {
						const urlObj = new URL(payload.url);
						const path = urlObj.pathname.replace(/^\//, ""); // strip leading /
						const parts = path.split("/").filter(Boolean);
						if (parts.length >= 2) {
							workspaceId = parts[0];
							eventId = parts[1];
						} else if (parts.length === 1) {
							eventId = parts[0];
						}
						searchParams = urlObj.searchParams;
					} catch {
						const rest = payload.url.replace("pique://notification/", "");
						const [pathOnly, query = ""] = rest.split("?");
						const parts = (pathOnly ?? "").split("/").filter(Boolean);
						if (parts.length >= 2) {
							workspaceId = parts[0];
							eventId = parts[1];
						} else if (parts.length === 1) {
							eventId = parts[0];
						}
						searchParams = new URLSearchParams(query);
					}

					if (!eventId) {
						posthog.capture("notification_deeplink_dropped", {
							reason: "missing_event_id",
							url: dedupeKey,
						});
						return;
					}

					const get = (k: string) => {
						const v = searchParams?.get(k);
						if (v === undefined || v === null) return undefined;
						// Treat empty string as undefined for optional fields
						return v === "" ? undefined : v;
					};

					const parseJsonish = (raw?: string): unknown => {
						if (!raw) return undefined;
						try {
							return JSON.parse(raw);
						} catch {
							// Try to normalize single-quoted JSON into valid JSON
							const trimmed = raw.trim();
							const looksJson =
								(trimmed.startsWith("{") && trimmed.endsWith("}")) ||
								(trimmed.startsWith("[") && trimmed.endsWith("]"));
							if (!looksJson) return undefined;
							try {
								// Replace 'key': and : 'value' patterns, then any remaining '...'
								const s = trimmed
									.replace(/'([^']*)'\s*:/g, '"$1":')
									.replace(/:\s*'([^']*)'/g, ': "$1"')
									.replace(/'([^']*)'/g, '"$1"');
								return JSON.parse(s);
							} catch {
								return undefined;
							}
						}
					};

					const attendeesParsed = CalendarAttendeeSchema.array().safeParse(
						parseJsonish(get("attendees")),
					);
					const conferenceParsed = ConferenceDataSchema.safeParse(
						parseJsonish(get("conferenceData")),
					);

					const normalizedNullable = (v?: string) =>
						typeof v === "string" ? (v.length ? v : null) : undefined;

					const candidate: any = {
						id: eventId,
						summary: get("summary") || "Untitled Event",
						description: normalizedNullable(get("description")),
						location: normalizedNullable(get("location")),
						startTime: get("startTime") || new Date().toISOString(),
						endTime: get("endTime") || new Date().toISOString(),
						timeZone: normalizedNullable(get("timeZone")),
						hangoutLink: normalizedNullable(get("hangoutLink")),
					};
					if (attendeesParsed.success) {
						candidate.attendees = attendeesParsed.data;
					}
					if (conferenceParsed.success) {
						candidate.conferenceData = conferenceParsed.data;
					}
					const source = get("source");
					if (source) candidate.source = source as any;
					const calendarId = get("calendarId");
					if (calendarId) candidate.calendarId = calendarId;

					const parsed = CalendarEventSchema.safeParse(candidate);
					if (!parsed.success) {
						posthog.capture("notification_deeplink_dropped", {
							reason: "event_parse_failed",
							url: dedupeKey,
							issues: parsed.error.issues
								.map((issue) => `${issue.path.join(".")}: ${issue.code}`)
								.join("; "),
						});
						return;
					}

					import("@pique/core/services/transcription-service")
						.then(({ TranscriptionService }) => {
							try {
								const transcriptionService =
									frameworkProvider.get(TranscriptionService);
								const ws =
									workspaceId ??
									(localStorage.getItem("last_workspace_id") || undefined);
								transcriptionService.startMeetingTranscribing(
									ws || "unknown",
									parsed.data,
								);
							} catch (error) {
								console.error("Error calling transcription service:", error);
							}
						})
						.catch(console.error);
				}
			};

			listen("deeplink-received", (event) => {
				handleDeeplinkUrl((event.payload as { url: string }).url);
			})
				.then(() => import("@tauri-apps/api/core"))
				.then(({ invoke }) => invoke<string[]>("take_pending_deeplinks"))
				.then((urls) => urls.forEach(handleDeeplinkUrl))
				.catch(console.error);
		})
		.catch(console.error);
}

function AppContent() {
	return <MainAppContent />;
}

const MainAppContent = () => {
	const authService = useService(AuthService);
	const account = useLiveData(authService.session.account$);

	const content = (
		<>
			<CustomThemeModifier />
			<DebugProvider>
				<GlobalLoading />
				<RouterProvider
					fallbackElement={<AppFallback key="RouterFallback" />}
					router={router}
					future={future}
				/>
			</DebugProvider>
		</>
	);

	if (account?.id) {
		return (
			<NovuProvider
				applicationIdentifier={NOVU_APP_IDENTIFIER}
				subscriber={account.id}
			>
				{content}
			</NovuProvider>
		);
	}

	return content;
};

export function App() {
	if (!languageLoadingPromise) {
		languageLoadingPromise = loadLanguage().catch(console.error);
	}

	return (
		<PiqueErrorBoundary>
			<Suspense>
				<FrameworkRoot framework={frameworkProvider}>
					<CacheProvider value={cache}>
						<AffineContext store={getCurrentStore()}>
							<AppContent />
						</AffineContext>
					</CacheProvider>
				</FrameworkRoot>
			</Suspense>
		</PiqueErrorBoundary>
	);
}
